import _isEqual from 'lodash.isequal';
import omit from 'lodash.omit';
import {
  Category,
  CreateFormTemplateFieldGroupRequest,
  CreateFormTemplateFieldRequest,
  FormTemplateAttribute,
  generateFormSlug,
} from 'mapistry-shared';
import { useCallback, useEffect, useState } from 'react';
import { DEFAULT_STARTER_FORM_TEMPLATE } from '../../../GenerateFormTemplatePage/defaultStarterFormTemplate';
import {
  CreateFormTemplateFieldGroupFormValues,
  CreateFormTemplateFieldRequestValues,
  EditableFormTemplate,
  EditableFormTemplateField,
  EditableFormTemplateFieldGroup,
  FieldGroupSlug,
  FieldWithGroupSlug,
  UpdateFormTemplateFieldGroupFormValues,
} from '../../types';
import { useFieldConditionHelpers } from '../useFieldConditionHelpers';
import { useGetInspectionDate } from '../useGetInspectionDate';
import { getFieldCreateRequest } from './newFieldHelpers';
import { useCopyField } from './useCopyField';
import { useCopyGroup } from './useCopyGroup';

export const useEditableFormTemplateState = ({
  passedInFormTemplate,
  setIsDirty,
}: {
  passedInFormTemplate?: EditableFormTemplate;
  setIsDirty: (isDirty: boolean) => void;
}) => {
  const [formTemplate, setFormTemplate] = useState<EditableFormTemplate>(
    passedInFormTemplate || DEFAULT_STARTER_FORM_TEMPLATE,
  );
  /** using slug instead of id, since group slugs stay consistent between versioned saves */
  const [activeGroupSlug, setActiveGroupSlug] = useState<FieldGroupSlug>();
  const { inspectionDateField, inspectionDateGroupSlug } =
    useGetInspectionDate(formTemplate);
  const fieldConditionHelpers = useFieldConditionHelpers(formTemplate);

  useEffect(() => {
    if (passedInFormTemplate) {
      setFormTemplate(passedInFormTemplate);
      setIsDirty(false);
    }
  }, [passedInFormTemplate, setIsDirty]);

  useEffect(() => {
    const stillHasGroupWithActiveSlug = formTemplate?.groups?.some(
      (group) => group.slug === activeGroupSlug,
    );
    if (!stillHasGroupWithActiveSlug) {
      setActiveGroupSlug(formTemplate?.groups?.[0]?.slug);
    }
  }, [formTemplate, activeGroupSlug]);

  const getEqualsDefaultStarterTemplate = useCallback(
    () => _isEqual(formTemplate.groups, DEFAULT_STARTER_FORM_TEMPLATE.groups),
    [formTemplate.groups],
  );

  const updateBasicInfo = useCallback(
    (
      updatedName: string,
      updatedSlug: string,
      updatedCategory: Category | null,
    ) => {
      const isChanged =
        formTemplate.name !== updatedName ||
        formTemplate.slug !== updatedSlug ||
        formTemplate.category !== updatedCategory;
      if (!isChanged) return;

      setFormTemplate((oldFormTemplate) => {
        if (!oldFormTemplate) return oldFormTemplate;
        return {
          ...oldFormTemplate,
          name: updatedName,
          slug: updatedSlug,
          category: updatedCategory,
        };
      });
      setIsDirty(true);
    },
    [formTemplate.name, formTemplate.slug, formTemplate.category, setIsDirty],
  );

  const updateGroupInfo = useCallback(
    (updatedGroupValues: UpdateFormTemplateFieldGroupFormValues) => {
      setFormTemplate((oldFormTemplate) => {
        if (!oldFormTemplate) return oldFormTemplate;

        const updatedGroups = oldFormTemplate.groups.map((g) => {
          if (g.slug !== activeGroupSlug) {
            return g;
          }

          const isNoLongerDuplicable =
            g.isDuplicable && !updatedGroupValues.isDuplicable;

          // No more duplicable display fields if the group is no longer duplicable
          const updatedFields = isNoLongerDuplicable
            ? g.fields.map((f) => ({
                ...f,
                isDuplicableDisplayField: false,
              }))
            : g.fields;

          return {
            ...g,
            ...updatedGroupValues,
            isStencilProtected: updatedGroupValues.isDuplicable
              ? !!updatedGroupValues.isStencilProtected
              : false,
            fields: updatedFields,
          };
        });

        return { ...oldFormTemplate, groups: updatedGroups };
      });
      setIsDirty(true);
    },
    [activeGroupSlug, setIsDirty],
  );

  const updateGroupOrder = useCallback(
    // ordering by slug not id, so new groups don't need to have an id generated on the frontend
    (updatedGroupOrder: FieldGroupSlug[]) => {
      setFormTemplate((oldFormTemplate) => {
        if (!oldFormTemplate) return oldFormTemplate;

        const newlyOrderedGroups = updatedGroupOrder.map(
          (groupSlug) =>
            // we know that only valid group ids are in the group order
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            oldFormTemplate.groups.find((g) => g.slug === groupSlug)!,
        );

        return { ...oldFormTemplate, groups: newlyOrderedGroups };
      });
      setIsDirty(true);
    },
    [setIsDirty],
  );

  const updateFieldOrder = useCallback(
    (updatedFieldOrder: string[]) => {
      setFormTemplate((oldFormTemplate) => {
        if (!oldFormTemplate) return oldFormTemplate;

        const updatedGroups = oldFormTemplate.groups.map((group) =>
          group.slug === activeGroupSlug
            ? {
                ...group,
                fields: updatedFieldOrder.map(
                  // we know that only valid field ids are in the field order
                  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                  (fieldId) => group.fields.find((f) => f.id === fieldId)!,
                ),
              }
            : group,
        );

        return { ...oldFormTemplate, groups: updatedGroups };
      });

      setIsDirty(true);
    },
    [activeGroupSlug, setIsDirty],
  );

  const updateField = useCallback(
    (
      updatedField: EditableFormTemplateField,
      updatedFieldGroupSlug?: FieldGroupSlug,
    ) => {
      setFormTemplate((oldFormTemplate) => {
        if (!oldFormTemplate) return oldFormTemplate;
        return {
          ...oldFormTemplate,
          groups: oldFormTemplate.groups.map((group) =>
            group.slug === (updatedFieldGroupSlug ?? activeGroupSlug)
              ? {
                  ...group,
                  fields: group.fields.map((f) =>
                    f.id === updatedField.id ? updatedField : f,
                  ),
                }
              : group,
          ),
        };
      });
      setIsDirty(true);
    },
    [activeGroupSlug, setIsDirty],
  );

  const removeFieldFromConditionalRules = useCallback(
    (fieldToDelete: EditableFormTemplateField) => {
      const triggerField =
        fieldConditionHelpers.getTriggerFieldByConditionallyShownFieldId(
          fieldToDelete.id,
        );
      if (!triggerField) return;

      const updatedConditionalRules = triggerField.conditionalRules
        ?.map((rule) => ({
          ...rule,
          conditionallyShownFields: rule.conditionallyShownFields.filter(
            (f) => f.id !== fieldToDelete.id,
          ),
        }))
        // remove all rules that just had their last conditionally shown field removed
        ?.filter((rule) => rule.conditionallyShownFields.length > 0);

      const updatedField: EditableFormTemplateField = {
        ...triggerField,
        conditionalRules: updatedConditionalRules,
      };

      updateField(updatedField, activeGroupSlug);
    },
    [fieldConditionHelpers, updateField, activeGroupSlug],
  );

  const deleteField = useCallback(
    (
      fieldToDelete: EditableFormTemplateField,
      fieldToDeleteGroupSlug?: FieldGroupSlug,
    ) => {
      removeFieldFromConditionalRules(fieldToDelete);
      setFormTemplate((oldFormTemplate) => {
        if (!oldFormTemplate) return oldFormTemplate;

        return {
          ...oldFormTemplate,
          groups: oldFormTemplate.groups.map((group) =>
            group.slug === (fieldToDeleteGroupSlug ?? activeGroupSlug)
              ? {
                  ...group,
                  fields: group.fields.filter((f) => f.id !== fieldToDelete.id),
                }
              : group,
          ),
        };
      });
      setIsDirty(true);
    },
    [activeGroupSlug, removeFieldFromConditionalRules, setIsDirty],
  );

  const addField = useCallback(
    (
      newFieldValues: CreateFormTemplateFieldRequestValues,
      newFieldGroupSlug?: FieldGroupSlug,
    ) => {
      const newField = getFieldCreateRequest(newFieldValues);
      setFormTemplate((oldFormTemplate) => {
        if (!oldFormTemplate) return oldFormTemplate;
        return {
          ...oldFormTemplate,
          groups: oldFormTemplate.groups.map((group) =>
            group.slug === (newFieldGroupSlug ?? activeGroupSlug)
              ? {
                  ...group,
                  fields: [...group.fields, newField],
                }
              : group,
          ),
        };
      });
      setIsDirty(true);
    },
    [activeGroupSlug, setIsDirty],
  );

  const addFieldsAtIndex = useCallback(
    (
      newFields: CreateFormTemplateFieldRequest[],
      index: number,
    ): CreateFormTemplateFieldRequest[] => {
      setFormTemplate((oldFormTemplate) => {
        if (!oldFormTemplate) return oldFormTemplate;

        return {
          ...oldFormTemplate,
          groups: oldFormTemplate.groups.map((group) =>
            group.slug === activeGroupSlug
              ? {
                  ...group,
                  fields: [
                    ...group.fields.slice(0, index),
                    ...newFields,
                    ...group.fields.slice(index),
                  ],
                }
              : group,
          ),
        };
      });

      setIsDirty(true);
      return newFields;
    },
    [activeGroupSlug, setIsDirty],
  );

  const addGroup = useCallback(
    (
      group: CreateFormTemplateFieldGroupFormValues,
      groupFields: CreateFormTemplateFieldRequest[],
      insertAtIndex?: number,
    ) => {
      const newGroupSlug = generateFormSlug();
      const newGroup: CreateFormTemplateFieldGroupRequest = {
        ...group,
        isDuplicable: !!group.isDuplicable,
        isStencilProtected: !!group.isDuplicable && !!group.isStencilProtected,
        slug: newGroupSlug,
        fields: groupFields,
      };

      setFormTemplate((oldFormTemplate) => {
        if (!oldFormTemplate) return oldFormTemplate;
        const newGroups =
          insertAtIndex === undefined
            ? [...oldFormTemplate.groups, newGroup]
            : [
                ...oldFormTemplate.groups.slice(0, insertAtIndex),
                newGroup,
                ...oldFormTemplate.groups.slice(insertAtIndex),
              ];

        return {
          ...oldFormTemplate,
          groups: newGroups,
        };
      });
      setActiveGroupSlug(newGroupSlug);
      setIsDirty(true);
    },
    [setIsDirty],
  );

  const removePreviousInspectionDateSetting = useCallback(() => {
    if (!inspectionDateField) return;
    updateField(
      {
        ...inspectionDateField,
        attributes: inspectionDateField.attributes?.filter(
          (attr) => attr !== FormTemplateAttribute.IS_INSPECTION_DATE,
        ),
      },
      inspectionDateGroupSlug,
    );
  }, [inspectionDateField, inspectionDateGroupSlug, updateField]);

  const setInspectionDate = useCallback(
    ({
      field: newInspectionDateField,
      groupSlug: newInspectionDateFieldGroupSlug,
    }: FieldWithGroupSlug) => {
      if (newInspectionDateField.id === inspectionDateField?.id) {
        return;
      }
      removePreviousInspectionDateSetting();
      updateField(
        {
          ...newInspectionDateField,
          isRequired: true,
          attributes: [
            ...(newInspectionDateField.attributes ?? []),
            FormTemplateAttribute.IS_INSPECTION_DATE,
          ],
        },
        newInspectionDateFieldGroupSlug,
      );
      setIsDirty(true);
    },
    [
      inspectionDateField,
      setIsDirty,
      updateField,
      removePreviousInspectionDateSetting,
    ],
  );

  const moveInspectionDateToGroup = useCallback(
    (newGroupSlug: string) => {
      if (!inspectionDateField) return;
      const newInspectionDateField = omit(inspectionDateField, ['id', 'slug']);
      deleteField(inspectionDateField, inspectionDateGroupSlug);
      addField(newInspectionDateField, newGroupSlug);

      setIsDirty(true);
    },
    [
      inspectionDateField,
      deleteField,
      inspectionDateGroupSlug,
      addField,
      setIsDirty,
    ],
  );

  const deleteGroup = useCallback(
    (newInspectionDateGroupSlug?: string) => {
      if (activeGroupSlug === inspectionDateGroupSlug) {
        if (
          !newInspectionDateGroupSlug ||
          newInspectionDateGroupSlug === activeGroupSlug
        ) {
          throw new Error(
            'Cannot delete the section with the inspection date without specifying which section to move it to.',
          );
        }
        moveInspectionDateToGroup(newInspectionDateGroupSlug);
      }

      setFormTemplate((oldFormTemplate) => {
        if (!oldFormTemplate) return oldFormTemplate;

        const activeGroupIndex = oldFormTemplate.groups.findIndex(
          (g) => g.slug === activeGroupSlug,
        );
        const updatedGroups = oldFormTemplate.groups.filter(
          (g) => g.slug !== activeGroupSlug,
        );

        if (updatedGroups.length === 0) {
          throw new Error(
            'Deleted a section when there are no other sections remaining.',
          );
        }

        const newActiveGroup =
          // go to the next group (same index after the old group got deleted)
          updatedGroups[activeGroupIndex] ??
          // or if it's the last group, the new last group
          updatedGroups[updatedGroups.length - 1];

        if (newActiveGroup) {
          setActiveGroupSlug(newActiveGroup.slug);
        }

        return { ...oldFormTemplate, groups: updatedGroups };
      });
      setIsDirty(true);
    },
    [
      activeGroupSlug,
      inspectionDateGroupSlug,
      moveInspectionDateToGroup,
      setIsDirty,
    ],
  );

  const replaceAllGroupsAndFields = useCallback(
    (newFormTemplateGroups: EditableFormTemplateFieldGroup[]) => {
      setFormTemplate((oldFormTemplate) => {
        if (!oldFormTemplate) return oldFormTemplate;
        return {
          ...oldFormTemplate,
          groups: newFormTemplateGroups,
        };
      });
      setIsDirty(true);
    },
    [setIsDirty],
  );

  const copyField = useCopyField({
    activeGroupSlug,
    addFieldsAtIndex,
    fieldConditionHelpers,
    groups: formTemplate.groups,
    updateField,
  });

  const copyGroup = useCopyGroup({
    activeGroupSlug,
    addGroup,
    groups: formTemplate?.groups,
  });

  return {
    activeGroupSlug,
    addField,
    addGroup,
    deleteField,
    deleteGroup,
    copyField,
    copyGroup,
    fieldConditionHelpers,
    formTemplate,
    getEqualsDefaultStarterTemplate,
    inspectionDateField,
    inspectionDateGroupSlug,
    moveInspectionDateToGroup,
    replaceAllGroupsAndFields,
    setActiveGroupSlug,
    setInspectionDate,
    setIsDirty,
    updateBasicInfo,
    updateField,
    updateFieldOrder,
    updateGroupInfo,
    updateGroupOrder,
  };
};

export type FormTemplateHelpers = ReturnType<
  typeof useEditableFormTemplateState
>;
